import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEarthEurope } from "@fortawesome/free-solid-svg-icons";
import { localizePath } from "../../i18n/localizedPath";
import { blogPosts } from "../../consts/blogPosts";
import { saveLanguagePreference } from "../../utils/languagePreference";
import styles from "./languageSwitcher.module.scss";

type BlogPost = { slug: string; lang?: string; ptSlug?: string };

export function languageSwitchPath(pathname: string, search = "", hash = ""): string {
    const suffix = `${search}${hash}`;
    if (pathname === "/pt") return `/${suffix}`;
    if (pathname.startsWith("/pt/")) return `${pathname.slice(3)}${suffix}`;

    const blogSlug = pathname.startsWith("/blog/") ? pathname.slice("/blog/".length) : null;
    if (blogSlug) {
        const posts = blogPosts as BlogPost[];
        const englishTwin = posts.find((post) => post.ptSlug === blogSlug);
        if (englishTwin) return `/blog/${englishTwin.slug}${suffix}`;
        const post = posts.find((candidate) => candidate.slug === blogSlug);
        if (post?.ptSlug) return `/blog/${post.ptSlug}${suffix}`;
    }

    const localized = localizePath(`${pathname}${suffix}`, "pt");
    return localized === `${pathname}${suffix}` ? `/pt${suffix}` : localized;
}

type Variant = "full" | "compact" | "footer";

function LanguageSwitcher({ variant = "full", className }: { variant?: Variant; className?: string }) {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const isPt = i18n.language === "pt";
    const targetLang = isPt ? "en" : "pt";
    const to = languageSwitchPath(location.pathname, location.search, location.hash);
    const variantClass =
        variant === "footer" ? className : variant === "compact" ? `${styles.switcher} ${styles.compact}` : styles.switcher;

    return (
        <Link
            to={to}
            className={variantClass}
            lang={targetLang}
            hrefLang={targetLang}
            aria-label={t("header.language.switchLabel")}
            title={t("header.language.switchLabel")}
            onClick={() => saveLanguagePreference(targetLang)}
        >
            <FontAwesomeIcon icon={faEarthEurope} className={variant === "footer" ? styles.footerIcon : styles.icon} aria-hidden="true" />
            {variant === "full" ? (
                <span
                    className={`${styles.code} ${styles.fixedLabel}`}
                    data-reserve={t("header.language.targetName", { lng: targetLang })}
                    aria-hidden="true"
                >
                    <span>{t("header.language.targetName")}</span>
                </span>
            ) : (
                <span className={variant === "footer" ? undefined : styles.code} aria-hidden="true">
                    {variant === "compact" ? targetLang.toUpperCase() : t("header.language.targetName")}
                </span>
            )}
        </Link>
    );
}

export default LanguageSwitcher;
